import React, {
  useEffect,
  useRef,
  useState
} from "react";

import { useNavigate, useLocation } from "react-router-dom";

import NewsLetter from "../elements/NewsLetter";
import PageTitle from "../elements/PageTitle";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL;

const Packages = () => {
  const [status, setStatus] = useState(
    "Processing payment..."
  );

  const navigate = useNavigate();
  const location = useLocation();

  // Prevent duplicate capture requests.
  const captureStarted = useRef(false);

  useEffect(() => {
    if (captureStarted.current) {
      return;
    }

    captureStarted.current = true;

    const query =
      new URLSearchParams(
        location.search
      );

    // PayPal normally returns ?token=ORDER_ID
    const orderId =
      query.get("token") ||
      query.get("orderId");

    const urlPackId =
      query.get("packId");

    const urlPlan =
      query.get("plan");

  

    // =========================================================
    // 1. Check order ID
    // =========================================================

    if (!orderId) {
      setStatus(
        "No PayPal order ID found."
      );

      console.error(
        "No PayPal order ID found."
      );

      return;
    }

    // =========================================================
    // 2. Get JWT
    // =========================================================

    const token =
      localStorage.getItem(
        "token"
      );

    if (!token) {
      setStatus(
        "User not logged in."
      );

      console.error(
        "JWT token not found."
      );

      return;
    }

    // =========================================================
    // 3. Get pending order information
    // =========================================================

    let pendingOrder = {};

    try {
      pendingOrder =
        JSON.parse(
          localStorage.getItem(
            "pendingPackOrder"
          ) || "{}"
        );
    } catch (error) {
      console.error(
        "Invalid pendingPackOrder:",
        error
      );

      pendingOrder = {};
    }

 
    const finalPackId =
      urlPackId ||
      pendingOrder.packId;

    const finalPlan =
      urlPlan ||
      pendingOrder.plan;

  

    // =========================================================
    // 4. Validate pack and plan
    // =========================================================

    if (!finalPackId) {
      setStatus(
        "Missing pack information."
      );

      console.error(
        "Missing packId."
      );

      return;
    }

    if (!finalPlan) {
      setStatus(
        "Missing subscription plan."
      );

      console.error(
        "Missing plan."
      );

      return;
    }

    // =========================================================
    // 5. Capture payment
    // =========================================================

    const captureOrder =
      async () => {
        try {
          setStatus(
            "Verifying payment..."
          );

          // =====================================================
          // Get current user
          // =====================================================

          const userRes =
            await fetch(
              `${API_BASE}/users/me`,
              {
                method: "GET",

                headers: {
                  "Content-Type":
                    "application/json",

                  Authorization:
                    `Bearer ${token}`,
                }
              }
            );

          const userData =
            await userRes
              .json()
              .catch(
                () => ({})
              );


          if (!userRes.ok) {
            throw new Error(
              userData.message ||
              userData.error ||
              "Failed to fetch user data"
            );
          }

          // =====================================================
          // Capture PayPal payment
          // =====================================================

          setStatus(
            "Completing payment..."
          );

          const res =
            await fetch(
              `${API_BASE}/courses/packs/pay/capture`,
              {
                method: "POST",

                headers: {
                  "Content-Type":
                    "application/json",

                  // IMPORTANT
                  // Backend gets userId
                  // from this JWT.
                  Authorization:
                    `Bearer ${token}`
                },

                body:
                  JSON.stringify({
                    orderId:
                      orderId,

                    packId:
                      finalPackId,

                    plan:
                      finalPlan
                  })
              }
            );

          const data =
            await res
              .json()
              .catch(
                () => ({})
              );

          // =====================================================
          // Handle error
          // =====================================================

          if (!res.ok) {
            // PayPal approval required
            if (
              data.error ===
                "ORDER_NOT_APPROVED" &&
              data.approvalUrl
            ) {

              window.location.href =
                data.approvalUrl;

              return;
            }

            throw new Error(
              data.message ||
              data.error ||
              "Payment capture failed."
            );
          }

          // =====================================================
          // Payment successful
          // =====================================================

          // Remove pending payment
          localStorage.removeItem(
            "pendingPackOrder"
          );

          const categoryId =
            data.pack?.category_id;

          // =====================================================
          // Redirect using category
          // =====================================================

          if (categoryId) {
            setStatus(
              "Payment successful! Subscription activated. Redirecting..."
            );

            setTimeout(
              () => {
                navigate(
                  `/Categories/Category/${categoryId}/Pack/${finalPackId}/Courses`
                );
              },
              2000
            );

            return;
          }

          // =====================================================
          // Fallback: fetch pack
          // =====================================================

          try {
            const packRes =
              await fetch(
                `${API_BASE}/courses/packs/${finalPackId}`,
                {
                  method:
                    "GET",

                  headers: {
                    "Content-Type":
                      "application/json"
                  }
                }
              );

            const packData =
              await packRes
                .json()
                .catch(
                  () => ({})
                );

            if (
              packRes.ok
            ) {
              const catId =
                packData.category_id?._id ||
                packData.category_id;

              if (catId) {
                setStatus(
                  "Payment successful! Subscription activated. Redirecting..."
                );

                setTimeout(
                  () => {
                    navigate(
                      `/Categories/Category/${catId}/Pack/${finalPackId}/Courses`
                    );
                  },
                  2000
                );

                return;
              }
            }
          } catch (
            fetchError
          ) {
            console.error(
              "ERROR FETCHING PACK:",
              fetchError
            );
          }

          // =====================================================
          // Final fallback
          // =====================================================

          setStatus(
            "Payment successful! Subscription activated."
          );

          setTimeout(
            () => {
              navigate(
                "/Categories"
              );
            },
            3000
          );

        } catch (err) {
          console.error(
            "======================================"
          );

          console.error(
            "FRONTEND CAPTURE ERROR"
          );

          console.error(
            "MESSAGE:",
            err?.message
          );

          console.error(
            "ERROR:",
            err
          );

          console.error(
            "======================================"
          );

          setStatus(
            `Error: ${
              err?.message ||
              "Payment failed"
            }`
          );
        }
      };

    captureOrder();

  }, [
    location.search,
    navigate
  ]);

  return (
    <div
      className="page-content"
      style={{
        backgroundColor:
          "#fdffa2ff"
      }}
    >
      {/* Page Header */}

      <PageTitle
        parentTitle="Categories"
      />

      {/* Payment Status */}

      <div
        style={{
          padding: "2rem",
          textAlign: "center",
          minHeight: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "18px",
          fontWeight: "600"
        }}
      >
        {status}
      </div>

      {/* Newsletter */}

      <section className="call-action style-1 footer-action">
        <div className="container">
          <NewsLetter />
        </div>
      </section>
    </div>
  );
};

export default Packages;